import { getApiClientVigo } from "./ApiClientVigo.js";
import { useRechnerStore } from '@/stores/rechner.js';
import { useMainStore } from '@/stores/useMainStore.js';
import { useAntragStore } from '@/stores/antrag.js';

/**
 * Reicht Vertragsunterlagen zur Verarbeitung ein
 * @param {string} antragId - Die AntragId (GUID)
 * @param {Object} data - Die Dokumenten-Daten aus dem DokStore
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export async function submitVertragsunterlagen(antragId, data) {
    try {
        const client = getApiClientVigo();
        const rechnerStore = useRechnerStore();
        const mainStore = useMainStore();
        const antragStore = useAntragStore();
        const endpoint = '/v2/Tarifrechner/reichevertragsunterlagenein';
        
        // statusVerwendung hochzählen vor API-Call
        antragStore.statusVerwendung++;
        //console.log('[DokService] statusVerwendung erhöht auf:', antragStore.statusVerwendung);
        
        // Vertriebsweg und Variante berechnen
        let vertriebsweg = "DAVG";
        let variante = "V";
        
        if (mainStore.kanal === "vigoselect") {
            // ARN (ARNS/ARNF): Immer Variante "V", unabhängig von Kanal und Vertriebsweg
            if (mainStore.rechner !== "arn") {
                variante = "A";
            }
            if (rechnerStore.aok > 0) {
                // AOK Antrag
                vertriebsweg = "AOK";
            } else {
                // Normaler vigoselect Antrag
                vertriebsweg = "EndkundeAOK";
            }
        }
        
        //console.log('[DokService] Vertriebsweg:', vertriebsweg, 'Variante:', variante);
        
        // Vertragsstand aus Versicherungsbeginn (API erwartet: YYYY-MM-DD)
        // Unterstützt beide Formate: TT.MM.JJJJ (Kombirechner) und YYYY-MM-DD (AR20/ARN)
        let vertragsstand = '';
        if (rechnerStore.versicherungsbeginn) {
            const dateString = rechnerStore.versicherungsbeginn;
            
            // Prüfe ob bereits ISO-Format (YYYY-MM-DD)
            if (dateString.includes('-') && dateString.length === 10) {
                vertragsstand = dateString;
            } 
            // Ansonsten deutsches Format (TT.MM.JJJJ) konvertieren
            else {
                const parts = dateString.split('.');
                if (parts.length === 3) {
                    const tag = parts[0].padStart(2, '0');
                    const monat = parts[1].padStart(2, '0');
                    const jahr = parts[2];
                    vertragsstand = `${jahr}-${monat}-${tag}`;
                }
            }
        }
        //console.log('[DokService] Versicherungsbeginn (Original):', rechnerStore.versicherungsbeginn);
        //console.log('[DokService] Vertragsstand (API-Format):', vertragsstand);
        
        // Request Body zusammenstellen gemäß API-Dokumentation
        const requestBody = {
            Kanal: mainStore.kanal,
            Vertriebsweg: vertriebsweg,
            ListeTarife: data.tarife,
            Variante: variante,
            Vertragsstand: vertragsstand,
            VnVornameNachname: `${data.vnVorname} ${data.vnNachname}`,
            MitBeitragsuebersicht: false
        };
        
        // Debug: Aufruf-URL und Body
        //console.log(`[DokService] POST ${endpoint}?antragId=${antragId}`);
        //console.log('[DokService] Request Body:', JSON.stringify(requestBody, null, 2));
        
        const response = await client.post(endpoint, requestBody, {
            params: { antragId },
            validateStatus: function (status) {
                console.log(`[DokService] Response Status: ${status}`);
                return status < 500;
            }
        });
        
        // Debug: Response JSON
        //console.log('[DokService] Response Data:', JSON.stringify(response.data, null, 2));
        
        // Erfolg prüfen: status === 200 && ok === true && returncode === 0
        if (response.status === 200 && response.data?.ok === true && response.data?.returncode === 0) {
            console.log('[DokService] Vertragsunterlagen erfolgreich eingereicht');
            return {
                success: true
            };
        } else {
            // API-Fehler mit detaillierter Meldung
            let errorMsg = response.data?.nachricht || 'Fehler beim Einreichen der Vertragsunterlagen';
            if (response.data?.returncode) {
                errorMsg = `${errorMsg} (Code: ${response.data.returncode})`;
            }
            console.error('[DokService] API Error:', errorMsg, 'ReturnCode:', response.data?.returncode);
            return {
                success: false,
                error: errorMsg
            };
        }
    }
    catch (error) {
        console.error('[DokService] Exception beim Einreichen:', error.message);
        return {
            success: false,
            error: error.message || 'Netzwerkfehler beim Einreichen der Vertragsunterlagen'
        };
    }
}

/**
 * Holt die Vertragsunterlagen als PDF ab
 * @param {string} antragId - Die AntragId (GUID)
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export async function downloadVertragsunterlagen(antragId) {
    try {
        const mainStore = useMainStore();
        const antragStore = useAntragStore();
        const endpoint = '/v2/Tarifrechner/vertragsunterlagen';
        
        // statusVerwendung hochzählen vor API-Call
        antragStore.statusVerwendung++;
        console.log('[DokService] statusVerwendung erhöht auf:', antragStore.statusVerwendung);
        
        // API gibt direkt PDF zurück (Content-Type: inline/application-pdf)
        const baseURL = mainStore.apiVigotr || '';
        const apiKey = mainStore.apiKey || '';
        const normalizedBase = baseURL.endsWith('/') ? baseURL.slice(0, -1) : baseURL;
        const fullUrl = `${normalizedBase}${endpoint}?antragId=${antragId}`;
        
        // Debug: Aufruf-URL
        console.log(`[DokService] GET ${fullUrl}`);
        
        const response = await fetch(fullUrl, {
            method: 'GET',
            headers: {
                'ApiKey': apiKey,
                'Accept': 'application/pdf'
            }
        });
        
        console.log('[DokService] Response Status:', response.status);
        console.log('[DokService] Content-Type:', response.headers.get('Content-Type'));
        
        if (!response.ok) {
            console.error('[DokService] PDF-Download fehlgeschlagen, Status:', response.status);
            
            // Versuche JSON-Fehlerantwort zu lesen (für Fehlerfälle)
            let errorMsg = 'PDF konnte nicht heruntergeladen werden';
            try {
                const errorData = await response.json();
                console.log('[DokService] Error Response Data:', JSON.stringify(errorData, null, 2));
                if (errorData?.nachricht) {
                    errorMsg = `${errorMsg}: ${errorData.nachricht}`;
                }
                if (errorData?.returncode) {
                    errorMsg = `${errorMsg} (Code: ${errorData.returncode})`;
                }
            } catch (e) {
                // Falls keine JSON-Response, bleibt die generische Meldung
                console.log('[DokService] Keine JSON-Fehlerantwort verfügbar');
            }
            
            return {
                success: false,
                error: errorMsg
            };
        }
        
        // Erfolgreicher Download - Response ist direkt das PDF als Blob
        const blob = await response.blob();
        console.log('[DokService] PDF erfolgreich als Blob empfangen, Größe:', blob.size, 'bytes');
        
        // PDF-Download auslösen
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'Vertragsdokumente.pdf';
        document.body.appendChild(link);
        link.click();
        
        // Cleanup
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        
        console.log('[DokService] PDF-Download erfolgreich ausgelöst');
        
        return {
            success: true
        };
    }
    catch (error) {
        console.error('[DokService] Exception beim Download:', error.message);
        return {
            success: false,
            error: error.message || 'Netzwerkfehler beim Herunterladen der Vertragsunterlagen'
        };
    }
}

/**
 * Wrapper-Funktion: Reicht Vertragsunterlagen ein und lädt sie herunter
 * @param {string} antragId - Die AntragId (GUID)
 * @param {Object} data - Die Dokumenten-Daten aus dem DokStore
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export async function generateAndDownloadPDF(antragId, data) {    
    
    // Schritt 1: Vertragsunterlagen einreichen
    const submitResult = await submitVertragsunterlagen(antragId, data);
    
    if (!submitResult.success) {
        console.error('[DokService] Einreichen fehlgeschlagen:', submitResult.error);
        
        return {
            success: false,
            error: submitResult.error
        };
    }
    
    // 3 Sekunden warten
    await new Promise(resolve => setTimeout(resolve, 3000));
    
    // Schritt 2: PDF herunterladen
    const downloadResult = await downloadVertragsunterlagen(antragId);
    
    if (!downloadResult.success) {
        console.error('[DokService] Download fehlgeschlagen:', downloadResult.error);
        return {
            success: false,
            error: downloadResult.error
        };
    }        
    
    return {
        success: true
    };
}
